import React from 'react'
import { Box, Card, Typography, useTheme, useMediaQuery, Container } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import InventoryIcon from '@mui/icons-material/Inventory'
import SettingsIcon from '@mui/icons-material/Settings'
import PersonIcon from '@mui/icons-material/Person'
import BarChartIcon from '@mui/icons-material/BarChart'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import UserLayout from '../../layouts/UserLayout'

const menuItems = [
	{
		id: 'product',
		title: 'Sản phẩm',
		subtitle: 'Quản lý sản phẩm',
		icon: InventoryIcon,
		bgGradient: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
		iconColor: '#FFFFFF',
		cardShadow: '0 8px 32px rgba(255, 107, 107, 0.3)',
		hoverShadow: '0 12px 40px rgba(255, 107, 107, 0.4)',
	},
	{
		id: 'config',
		title: 'Cấu hình',
		subtitle: 'Thiết lập hệ thống',
		icon: SettingsIcon,
		bgGradient: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
		iconColor: '#FFFFFF',
		cardShadow: '0 8px 32px rgba(78, 205, 196, 0.3)',
		hoverShadow: '0 12px 40px rgba(78, 205, 196, 0.4)',
	},
	{
		id: 'customer',
		title: 'Người dùng',
		subtitle: 'Quản lý khách hàng',
		icon: PersonIcon,
		bgGradient: 'linear-gradient(135deg, #A8E6CF 0%, #7FCDCD 100%)',
		iconColor: '#FFFFFF',
		cardShadow: '0 8px 32px rgba(168, 230, 207, 0.3)',
		hoverShadow: '0 12px 40px rgba(168, 230, 207, 0.4)',
	},
	{
		id: 'orders',
		title: 'Đơn hàng',
		subtitle: 'Quản lý đơn hàng',
		icon: ShoppingCartIcon,
		bgGradient: 'linear-gradient(135deg, #9C27B0 0%, #E91E63 100%)',
		iconColor: '#FFFFFF',
		cardShadow: '0 8px 32px rgba(156, 39, 176, 0.3)',
		hoverShadow: '0 12px 40px rgba(156, 39, 176, 0.4)',
	},
	{
		id: 'statistics',
		title: 'Thống kê',
		subtitle: 'Báo cáo & phân tích',
		icon: BarChartIcon,
		bgGradient: 'linear-gradient(135deg, #FFD93D 0%, #FF6B6B 100%)',
		iconColor: '#FFFFFF',
		cardShadow: '0 8px 32px rgba(255, 217, 61, 0.3)',
		hoverShadow: '0 12px 40px rgba(255, 217, 61, 0.4)',
	},
]

const ZaloMiniApp = () => {
	const navigate = useNavigate()
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

	const handleSelect = (type) => {
		switch (type) {
			case 'product':
				navigate('/admin/zalo-mini-app/product')
				break
			case 'config':
				navigate('/admin/zalo-mini-app/config')
				break
			case 'customer':
				navigate('/admin/zalo-mini-app/customer')
				break
			case 'orders':
				alert('Đang phát triển')
				break
			case 'statistics':
				navigate('/admin/zalo-mini-app/statistics')
				break
			default:
				break
		}
	}

	return (
		<UserLayout>
			<Container maxWidth="lg" sx={{ py: 4 }}>
				{/* Header Section */}
				<Box sx={{ textAlign: 'center', mb: 6 }}>
					<Typography
						variant="h3"
						sx={{
							fontWeight: 700,
							background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
							backgroundClip: 'text',
							WebkitBackgroundClip: 'text',
							WebkitTextFillColor: 'transparent',
							mb: 2,
							fontSize: { xs: '2rem', md: '3rem' },
						}}>
						Zalo Mini App
					</Typography>
					<Typography
						variant="h6"
						sx={{
							color: 'text.secondary',
							fontWeight: 400,
							maxWidth: '600px',
							margin: '0 auto',
							lineHeight: 1.6,
						}}>
						Quản lý toàn diện ứng dụng Zalo Mini App của bạn
					</Typography>
				</Box>

				{/* Menu Grid */}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
						gap: isMobile ? 3 : 4,
						maxWidth: '1200px',
						margin: '0 auto',
						justifyItems: 'center',
					}}>
					{menuItems.map((item) => (
						<Box
							key={item.id}
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								width: '100%',
								maxWidth: '280px',
							}}>
							<Card
								elevation={0}
								sx={{
									width: '100%',
									height: { xs: 120, md: 140 },
									borderRadius: '20px',
									background: item.bgGradient,
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									cursor: 'pointer',
									transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
									boxShadow: item.cardShadow,
									position: 'relative',
									overflow: 'hidden',
									'&::before': {
										content: '""',
										position: 'absolute',
										top: 0,
										left: 0,
										right: 0,
										bottom: 0,
										background: 'rgba(255, 255, 255, 0.1)',
										opacity: 0,
										transition: 'opacity 0.3s ease',
									},
									'&:hover': {
										transform: 'translateY(-8px) scale(1.02)',
										boxShadow: item.hoverShadow,
										'&::before': {
											opacity: 1,
										},
									},
								}}
								onClick={() => handleSelect(item.id)}>
								<Box
									sx={{
										background: 'rgba(255, 255, 255, 0.2)',
										borderRadius: '50%',
										width: 60,
										height: 60,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										mb: 1,
										backdropFilter: 'blur(10px)',
									}}>
									{React.createElement(item.icon, {
										sx: { 
											fontSize: 32, 
											color: item.iconColor,
											filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
										},
									})}
								</Box>
							</Card>
							
							{/* Title and Subtitle */}
							<Box sx={{ textAlign: 'center', mt: 2, width: '100%' }}>
								<Typography
									sx={{
										fontWeight: 600,
										fontSize: '1.1rem',
										color: 'text.primary',
										mb: 0.5,
									}}>
									{item.title}
								</Typography>
								<Typography
									variant="body2"
									sx={{
										color: 'text.secondary',
										fontSize: '0.875rem',
										lineHeight: 1.4,
									}}>
									{item.subtitle}
								</Typography>
							</Box>
						</Box>
					))}
				</Box>

				{/* Additional Info Section */}
				
			</Container>
		</UserLayout>
	)
}

export default ZaloMiniApp
